/**
 * FileMakerIntegration.ts - FileMaker XML clipboard integration
 * 
 * CHANGE HISTORY:
 * - 251102_1200 [pending] danielbeer: Created FileMaker integration for Clippr rebuild
 * 
 * Handles FileMaker XML (fmxmlsnippet) clipboard operations integrated with ClipprManager.
 */

import type { ClipboardItem, FileMakerClipboardItem, FileMakerObjectType } from '../types';

/**
 * FileMaker XML clipboard integration
 * Works with ClipprManager to handle FileMaker-specific clipboard operations
 */
export class FileMakerIntegration {
  
  /**
   * Parse FileMaker XML to extract metadata
   */
  parseFileMakerXML(xml: string): FileMakerClipboardItem {
    const item: FileMakerClipboardItem = {
      xml,
      type: 'Unknown',
      metadata: {}
    };
    
    try {
      // Detect object type from XML structure
      if (xml.includes('<Script ')) {
        item.type = 'Script';
        // Extract script name
        const nameMatch = xml.match(/name="([^"]+)"/);
        if (nameMatch) {
          item.name = nameMatch[1];
        }
      } else if (xml.includes('<CustomFunction ')) {
        item.type = 'CustomFunction';
        const nameMatch = xml.match(/name="([^"]+)"/);
        if (nameMatch) {
          item.name = nameMatch[1];
        }
      } else if (xml.includes('<Layout ')) {
        item.type = 'Layout';
        const nameMatch = xml.match(/name="([^"]+)"/);
        if (nameMatch) {
          item.name = nameMatch[1];
        }
      } else if (xml.includes('<BaseTable ')) {
        item.type = 'BaseTable';
        const nameMatch = xml.match(/name="([^"]+)"/);
        if (nameMatch) {
          item.name = nameMatch[1];
        }
      } else if (xml.includes('<Field ')) {
        item.type = 'Field';
        const nameMatch = xml.match(/name="([^"]+)"/);
        if (nameMatch) {
          item.name = nameMatch[1];
        }
      }
      
      // Extract ID if present
      const idMatch = xml.match(/id="([^"]+)"/);
      if (idMatch) {
        item.id = idMatch[1];
      }
      
      // Store additional metadata
      item.metadata = {
        hasSteps: xml.includes('<Step '),
        hasParameters: xml.includes('<Parameter '),
        hasLayouts: xml.includes('<Layout '),
        hasFields: xml.includes('<Field '),
        xmlLength: xml.length
      };
      
    } catch (error) {
      console.warn('Failed to parse FileMaker XML metadata:', error);
    }
    
    return item;
  }
  
  /**
   * Validate FileMaker XML structure
   */
  isValidFileMakerXML(xml: string): boolean {
    if (!xml || typeof xml !== 'string') {
      return false;
    }
    
    // Must contain fmxmlsnippet root element
    if (!xml.includes('fmxmlsnippet')) {
      return false;
    }
    
    // Must be well-formed XML (basic check)
    const openTags = (xml.match(/<[^/][^>]*>/g) || []).length;
    const closeTags = (xml.match(/<\/[^>]+>/g) || []).length;
    const selfClosing = (xml.match(/<[^>]+\/>/g) || []).length;
    
    // Open tags should equal close tags + self-closing tags
    return openTags === closeTags + selfClosing;
  }
  
  /**
   * Convert FileMaker XML to HTML for rich text display
   */
  xmlToHTML(xml: string, item: FileMakerClipboardItem): string {
    const escapedXml = xml
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    
    return `
      <div class="filemaker-clipboard-item" data-type="${item.type}">
        <div class="filemaker-header">
          <strong>FileMaker ${item.type}</strong>
          ${item.name ? `<span class="name">${item.name}</span>` : ''}
        </div>
        <pre class="filemaker-xml">${escapedXml}</pre>
      </div>
    `;
  }
  
  /**
   * Create ClipboardItem from FileMaker XML
   */
  createClipboardItem(xml: string): Partial<ClipboardItem> {
    const fmItem = this.parseFileMakerXML(xml);
    
    return {
      data: xml,
      format: 'application/x-filemaker-xml',
      metadata: {
        fileMaker: fmItem,
        source: 'filemaker'
      }
    };
  }
  
  /**
   * Extract FileMaker data from ClipboardItem
   */
  extractFileMakerData(item: ClipboardItem): FileMakerClipboardItem | null {
    if (item.format !== 'application/x-filemaker-xml') {
      return null;
    }
    
    if (typeof item.data === 'string' && this.isValidFileMakerXML(item.data)) {
      return this.parseFileMakerXML(item.data);
    }
    
    return null;
  }
  
  /**
   * Write FileMaker XML to system clipboard
   */
  async writeToSystemClipboard(xml: string): Promise<boolean> {
    try {
      const item = this.parseFileMakerXML(xml);
      
      if (typeof navigator !== 'undefined' && navigator.clipboard) {
        // Create clipboard item with multiple formats
        const clipboardData = new ClipboardItem({
          // Plain text format (for text editors)
          'text/plain': new Blob([xml], { type: 'text/plain' }),
          
          // HTML format (for rich text editors)
          'text/html': new Blob([this.xmlToHTML(xml, item)], { type: 'text/html' }),
          
          // FileMaker-specific format
          'application/x-filemaker-xml': new Blob([xml], { type: 'application/x-filemaker-xml' })
        });
        
        await navigator.clipboard.write([clipboardData]);
        return true;
      }
      
      // Fallback: Just write as text
      if (typeof navigator !== 'undefined' && navigator.clipboard) {
        await navigator.clipboard.writeText(xml);
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Failed to write FileMaker XML to clipboard:', error);
      return false;
    }
  }
  
  /**
   * Read FileMaker XML from system clipboard
   */
  async readFromSystemClipboard(): Promise<string | null> {
    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard) {
        // Try to read as text first
        const text = await navigator.clipboard.readText();
        
        // Check if it's FileMaker XML
        if (this.isValidFileMakerXML(text)) {
          return text;
        }
        
        // Try to read clipboard items with multiple types
        const items = await navigator.clipboard.read();
        for (const item of items) {
          // Check for FileMaker-specific format
          if (item.types.includes('application/x-filemaker-xml')) {
            const blob = await item.getType('application/x-filemaker-xml');
            const xml = await blob.text();
            if (this.isValidFileMakerXML(xml)) {
              return xml;
            }
          }
          
          // Check plain text
          if (item.types.includes('text/plain')) {
            const blob = await item.getType('text/plain');
            const text = await blob.text();
            if (this.isValidFileMakerXML(text)) {
              return text;
            }
          }
        }
      }
      
      return null;
    } catch (error) {
      console.error('Failed to read FileMaker XML from clipboard:', error);
      return null;
    }
  }
  
  /**
   * Convert FileMaker object type to readable string
   */
  getObjectTypeLabel(type: FileMakerObjectType): string {
    const labels: Record<FileMakerObjectType, string> = {
      Script: 'Script',
      CustomFunction: 'Custom Function',
      Layout: 'Layout',
      BaseTable: 'Table',
      Field: 'Field',
      Unknown: 'Unknown Object'
    };
    
    return labels[type] || 'Unknown';
  }
}

// Singleton instance
let instance: FileMakerIntegration | null = null;

/**
 * Get singleton FileMaker integration instance
 */
export function getFileMakerIntegration(): FileMakerIntegration {
  if (!instance) {
    instance = new FileMakerIntegration();
  }
  return instance;
}
